"use client";

import { MessageSquare, Send, MapPin, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
export default function Quote() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const statusItems = [{
    label: "SHARJAH WAREHOUSE: ACTIVE"
  }, {
    label: "AJMAN LOGISTICS: READY"
  }, {
    label: "AL AIN SUPPLY: EXPANDING"
  }];
  return <section data-section-id="3755" id="request-a-bulk-quote" className="relative bg-background text-foreground overflow-hidden py-24 md:py-32 px-4 sm:px-8">
      {}
      <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/industrial_aluminum_profiles_warehouse.webp)] opacity-20" aria-hidden="true" />

      {}
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" aria-hidden="true" />

      {}
      <div className="absolute top-1/2 left-4 -translate-y-1/2 rotate-90 origin-left pointer-events-none select-none opacity-10 font-mono text-xs tracking-[0.5em] whitespace-nowrap">
        25.3463° N, 55.4209° E // WORLD SKY LOGISTICS HUB // BATCH_REF: 882-DXB
      </div>

      {}
      <div className="absolute top-6 left-6 w-6 h-6 pointer-events-none" aria-hidden="true">
        <div className="absolute top-0 left-0 w-full h-px bg-primary/40" />
        <div className="absolute top-0 left-0 h-full w-px bg-primary/40" />
      </div>
      <div className="absolute bottom-6 right-6 w-6 h-6 pointer-events-none" aria-hidden="true">
        <div className="absolute bottom-0 right-0 w-full h-px bg-primary/40" />
        <div className="absolute bottom-0 right-0 h-full w-px bg-primary/40" />
      </div>

      <div ref={ref} className={`relative z-10 max-w-7xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="font-mono text-xs tracking-widest text-primary mb-6 uppercase">
          [ &sect; PROCUREMENT / BULK SUPPLY ]
        </div>

        <h2 className="font-default font-bold uppercase tracking-tighter text-foreground mb-16" style={{
        fontSize: "clamp(2.5rem, 6vw, 5rem)",
        lineHeight: "1"
      }}>
          SECURE YOUR <span className="text-primary">SUPPLY CHAIN</span>
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {}
          <div className="lg:col-span-5 space-y-10">
            <div className="space-y-6">
              <h3 className="font-mono text-sm font-bold tracking-widest uppercase text-primary">Direct Support</h3>
              <div className="flex items-start gap-4 p-6 border border-border bg-card/50 rounded-lg">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <MessageSquare className="size-6 text-primary" />
                </div>
                <div>
                  <p className="font-bold text-lg mb-1">WhatsApp Procurement</p>
                  <p className="text-muted-foreground text-sm mb-4">Instant technical consultation for bulk orders and custom profile specifications.</p>
                  <Button variant="outline" className="rounded-lg border-primary/50 hover:bg-primary/10">
                    START CHAT
                  </Button>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h3 className="font-mono text-sm font-bold tracking-widest uppercase text-primary">Company Details</h3>
              <div className="grid gap-4 text-sm">
                <div className="flex items-center gap-3">
                  <MapPin className="size-4 text-primary" />
                  <span>Sharjah & Ajman Industrial Areas, UAE</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="size-4 text-primary" />
                  <span>+971 [Sales Department]</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="size-4 text-primary" />
                  <span>procurement@worldsky.ae</span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-3 font-mono text-[10px] tracking-widest text-foreground/60 uppercase pt-8 border-t border-border">
              {statusItems.map((item, i) => <div data-index={i} key={i} className="flex items-center gap-2">
                  <span className="relative flex size-1.5">
                    <span className="absolute inline-flex h-full w-full rounded-lg bg-primary opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-lg size-1.5 bg-primary" />
                  </span>
                  <span>{item.label}</span>
                </div>)}
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="bg-card border border-border p-8 md:p-10 rounded-lg shadow-xl">
              <FormProvider formId="bulk-quote-request" sectionName="Quote" defaultValues={{
              company_name: "",
              contact_name: "",
              email: "",
              phone: "",
              product: "",
              quantity: "",
              message: ""
            }} validationRules={{
              company_name: {
                required: true
              },
              contact_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              product: {
                required: true
              },
              quantity: {
                required: true
              }
            }} submitText="SUBMIT QUOTE REQUEST">
                <QuoteForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function QuoteForm() {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    successMessage,
    submitError
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center py-12 space-y-4">
        <div className="size-16 bg-primary/10 text-primary rounded-lg flex items-center justify-center mx-auto mb-6">
          <Send className="size-8" />
        </div>
        <h3 className="text-2xl font-bold uppercase tracking-tight">Request Received</h3>
        <p className="text-muted-foreground max-w-md mx-auto">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-widest">Company Name</FormLabel>
              <FormControl>
                <Input placeholder="LLC / Corp Name" className="rounded-lg bg-background border-border focus:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="contact_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-widest">Contact Person</FormLabel>
              <FormControl>
                <Input placeholder="Full Name" className="rounded-lg bg-background border-border focus:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-widest">Business Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="email@company.com" className="rounded-lg bg-background border-border focus:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-widest">Phone Number</FormLabel>
              <FormControl>
                <Input placeholder="+971 --- --- ---" className="rounded-lg bg-background border-border focus:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="product" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-widest">Product Category</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Aluminum Profiles" className="rounded-lg bg-background border-border focus:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="quantity" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-widest">Estimated Quantity</FormLabel>
              <FormControl>
                <Input placeholder="Tons / Units" className="rounded-lg bg-background border-border focus:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>
      <FormField control={control} name="message" render={({
      field
    }) => <FormItem className="mb-8">
            <FormLabel className="font-mono text-[10px] uppercase tracking-widest">Project Requirements</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your specific technical requirements or project scope..." className="min-h-[120px] rounded-lg bg-background border-border focus:ring-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 mb-6 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-lg">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full py-6 rounded-lg font-mono tracking-widest uppercase font-bold shadow-lg">
        {isSubmitting ? "PROCESSING..." : "SUBMIT QUOTE REQUEST"}
      </Button>
    </Form>;
}